import { useEffect, useRef, useState } from 'react';
import type { TaxConnectionView, TaxInvoiceView } from 'shared-models/browser';
import { taxAuthorityService, TAX_LIVE_EVENT, TaxLiveEvent } from '../../../../../services/tax-authority/tax-authority.service';

export type TaxAuthorityProps = { opened: boolean; onClose: () => void; tenantId: string; employeeSession: string | null; initialOrderId?: number | null };

export function useTaxAuthorityController({ opened, tenantId, employeeSession, initialOrderId }: TaxAuthorityProps) {
  const [connection, setConnection] = useState<TaxConnectionView | null>(null);
  const [invoice, setInvoice] = useState<TaxInvoiceView | null>(null);
  const [sellerVatNumber, setSellerVatNumber] = useState('');
  const orderId = initialOrderId ?? null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lock = useRef(false);
  const generation = useRef(0);
  const connectionRevision = useRef(0);
  const invoiceRevision = useRef(0);
  const selectedOrder = useRef(orderId);
  selectedOrder.current = orderId;
  const configuredSeller = connection?.sellerVatNumber ?? sellerVatNumber;

  useEffect(() => {
    if (!opened || !employeeSession) return;
    const current = ++generation.current;
    const revision = connectionRevision.current;
    setConnection(null);
    setInvoice(null);
    setError(null);
    setSellerVatNumber('');
    const onEvent = (raw: Event) => {
      const event = (raw as CustomEvent<TaxLiveEvent>).detail;
      if (event.type === 'tax.connection.updated' && event.connection.tenantId === tenantId) {
        connectionRevision.current += 1;
        setConnection((previous) => previous && Date.parse(previous.updatedAt) > Date.parse(event.connection.updatedAt) ? previous : event.connection);
      }
      if (event.type === 'tax.invoice.updated' && event.invoice.tenantId === tenantId && event.invoice.orderId === selectedOrder.current) {
        invoiceRevision.current += 1;
        setInvoice((previous) => previous && Date.parse(previous.updatedAt) > Date.parse(event.invoice.updatedAt) ? previous : event.invoice);
      }
    };
    window.addEventListener(TAX_LIVE_EVENT, onEvent);
    void taxAuthorityService.connection(tenantId, employeeSession).then((view) => {
      if (generation.current !== current) return;
      if (connectionRevision.current === revision) {
        setConnection(view);
        setSellerVatNumber(view.sellerVatNumber ?? '');
      }
    }).catch((loadError: unknown) => {
      if (generation.current === current) setError(loadError instanceof Error ? loadError.message : 'טעינת החיבור נכשלה');
    });
    return () => { generation.current += 1; window.removeEventListener(TAX_LIVE_EVENT, onEvent); };
  }, [opened, tenantId, employeeSession, initialOrderId]);

  useEffect(() => {
    setInvoice(null);
    if (!opened || !employeeSession || !Number.isInteger(orderId) || Number(orderId) <= 0) return;
    let canceled = false;
    const revision = invoiceRevision.current;
    void taxAuthorityService.invoice(tenantId, employeeSession, Number(orderId)).then((view) => {
      if (!canceled && revision === invoiceRevision.current) {
        setInvoice(view);
      }
    }).catch((loadError: unknown) => {
      if (!canceled) setError(loadError instanceof Error ? loadError.message : 'טעינת ההקצאה נכשלה');
    });
    return () => { canceled = true; };
  }, [opened, tenantId, employeeSession, orderId]);

  async function run(action: () => Promise<void>) {
    if (lock.current || !employeeSession) return;
    lock.current = true;
    setBusy(true);
    setError(null);
    try { await action(); }
    catch (actionError: unknown) { setError(actionError instanceof Error ? actionError.message : 'הפעולה נכשלה'); }
    finally { lock.current = false; setBusy(false); }
  }

  function connect() {
    if (lock.current || !employeeSession) return;
    const popup = window.open('about:blank', '_blank');
    if (!popup) { setError('יש לאפשר פתיחת חלון הזדהות'); return; }
    popup.opener = null;
    void run(async () => {
      try { popup.location.assign(await taxAuthorityService.connect(tenantId, employeeSession, { sellerVatNumber: configuredSeller })); }
      catch (connectError) { popup.close(); throw connectError; }
    });
  }

  return { connection, invoice, sellerVatNumber, setSellerVatNumber,
    busy, error, connect,
    disconnect: () => run(() => taxAuthorityService.disconnect(tenantId, employeeSession!)),
    canConnect: !!connection && /^\d{9}$/.test(configuredSeller) && connection.status !== 'disabled',
  };
}